/**
 * @file FilterChips.tsx
 * @description Фильтры-пилюли списка со счётчиком (phase19.31, шаг О4; SC45).
 *
 * Пилюля сужает СПИСОК («Мои / Все / Недостача») и не должна выглядеть как
 * кнопка действия:
 *  - высота `--chip-h`: 48 до 1024, компактные 32 с 1024, 56 у кладовщика
 *    до 1024 (переменная темы, своих высот компонент не задаёт);
 *  - выбранная — мягкая подложка и текст цвета действия, без заливки
 *    основным цветом; остальные — тонкий контур и серый текст;
 *  - тон «опасно» включается ТОЛЬКО при числе больше нуля: «Недостача 0»
 *    красным тревожит на пустом месте;
 *  - ряд переносится на следующую строку на всех ширинах. Прокрутки с
 *    затуханием больше нет (дефект приёмки 2026-10-02: пилюля обрезалась
 *    затуханием на середине слова, «До 30 дней», 4-я пилюля за экраном).
 *
 * На телефоне набор отборов, который не помещается в строку, убирается в
 * шторку «Фильтры · N» (`FilterSheetButton`) — подключается через `FilterBar`
 * (`chipsCollapse`), а не страницей вручную (phase19.31, решение для всего
 * склада: на 360 под фильтрами остаётся место для ≥ 5 записей списка).
 *
 * Кнопки с `aria-pressed` в группе: это переключатель отбора, а не вкладки
 * разделов (для них — `Tabs`).
 *
 * `FilterToggleChips` (phase19.31, зона «Склад») — тот же вид пилюли, но
 * каждая включается независимо: «Только с остатком», «Без места», «Быстрые
 * действия», «Архивные». Один вид на весь кабинет — классы общие.
 *
 * ```tsx
 * <FilterChips
 *   aria-label="Фильтр заданий"
 *   items={[
 *     { value: "mine", label: "Мои", count: 1 },
 *     { value: "all", label: "Все", count: 3 },
 *     { value: "shortage", label: "Недостача", count: 0, tone: "danger" },
 *   ]}
 *   value={tab}
 *   onChange={setTab}
 * />
 * ```
 */

import { cn } from "@/lib/utils";

/** Описание пилюли. */
export interface FilterChip<T extends string> {
  value: T;
  label: string;
  /** Счётчик; `undefined` — без числа. */
  count?: number;
  /** `danger` — красный тон, но только при `count > 0`. */
  tone?: "default" | "danger";
}

interface Props<T extends string> {
  items: FilterChip<T>[];
  value: T;
  onChange: (value: T) => void;
  "aria-label": string;
  className?: string;
}

/** Классы пилюли по состоянию — реестром: «выбрана» × «опасно». Общие с оглавлением групп `SettingsToc`. */
export const CHIP_CLASS = {
  idle: "bg-surface-lowest text-on-surface-variant [box-shadow:inset_0_0_0_1px_var(--border)] hover:bg-surface-low hover:text-on-surface",
  active: "bg-accent text-accent-foreground [box-shadow:inset_0_0_0_1px_color-mix(in_srgb,var(--primary)_35%,transparent)]",
  idleDanger: "bg-surface-lowest text-[var(--destructive-text)] [box-shadow:inset_0_0_0_1px_var(--destructive-line)] hover:bg-[var(--destructive-soft)]",
  activeDanger: "bg-[var(--danger-soft)] text-[var(--destructive-text)] [box-shadow:inset_0_0_0_1px_var(--destructive-line)]",
} as const;

/** Тон «опасно» действует только при ненулевом счётчике. */
function chipIsDanger(chip: FilterChip<string>): boolean {
  return chip.tone === "danger" && (chip.count ?? 0) > 0;
}

/** Общая геометрия пилюли: высота из темы, текст элементов управления. */
export const CHIP_BASE = cn(
  "inline-flex min-h-[var(--chip-h,3rem)] shrink-0 items-center gap-1.5 rounded-full px-3",
  "whitespace-nowrap text-[length:var(--fs-control,0.875rem)] font-medium outline-none transition-colors",
  "focus-visible:ring-2 focus-visible:ring-ring",
);

/**
 * Ряд пилюль: перенос на всех ширинах, без прокрутки и затухания.
 * `max-w-full` обязателен: внутри строчного flex-родителя (`flex flex-wrap`
 * рядом с «Фильтры · N» или «Архивные») ряд с `shrink-0` иначе берёт ширину
 * всех пилюль в одну строку и растягивает страницу (QA-5 D1/D2: 512px на 360).
 * `shrink-0` оставлен для flex-col родителя — там он держит высоту ряда.
 */
const ROW_CLASS = "flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2";

/** Одна пилюля: выбранность, тон и счётчик. */
function Chip({ label, count, active, danger, onClick }: {
  label: string;
  count?: number;
  active: boolean;
  danger: boolean;
  onClick: () => void;
}) {
  const key = `${active ? "active" : "idle"}${danger ? "Danger" : ""}` as keyof typeof CHIP_CLASS;
  return (
    <button
      type="button"
      aria-pressed={active}
      data-tone={danger ? "danger" : "default"}
      onClick={onClick}
      className={cn(CHIP_BASE, CHIP_CLASS[key])}
    >
      {label}
      {count !== undefined ? <span className="tabular-nums opacity-80">{count}</span> : null}
    </button>
  );
}

export function FilterChips<T extends string>({ items, value, onChange, className, "aria-label": ariaLabel }: Props<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-slot="filter-chips"
      className={cn(ROW_CLASS, className)}
    >
      {items.map((chip) => (
        <Chip
          key={chip.value}
          label={chip.label}
          count={chip.count}
          active={chip.value === value}
          danger={chipIsDanger(chip)}
          onClick={() => onChange(chip.value)}
        />
      ))}
    </div>
  );
}

/** Независимый переключатель-пилюля. */
export interface FilterToggle {
  key: string;
  label: string;
  pressed: boolean;
  onToggle: () => void;
  count?: number;
}

interface ToggleProps {
  items: FilterToggle[];
  "aria-label": string;
  className?: string;
}

/** Пилюли-переключатели: каждая включается сама по себе (вид — как у `FilterChips`). */
export function FilterToggleChips({ items, className, "aria-label": ariaLabel }: ToggleProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-slot="filter-chips"
      className={cn(ROW_CLASS, className)}
    >
      {items.map((item) => (
        <Chip key={item.key} label={item.label} count={item.count} active={item.pressed} danger={false} onClick={item.onToggle} />
      ))}
    </div>
  );
}
